import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check as CheckIcon, ChevronDown } from 'lucide-react';
import { useInViewport } from '@lib/hooks/useInViewport';
import {
  elegirPlanSuperador,
  getPlanSuperador,
  marcarInteresContactoMs2,
  type PlanComparable,
  type DesgloseIntegrantePrecio,
} from '@lib/services/portal.service';
import { TyCMs2Modal } from './TyCMs2Modal';
import { PlanMockupCard } from './PlanMockupCard';
import { WhatsAppBanner } from './WhatsAppBanner';
import { TYC_MS2_VERSION_ACTUAL } from '@lib/constants/tyc-ms2';

interface Props {
  token: string;
  onBack: () => void;
  /**
   * Recibe la decisión del titular para que el caller pueda decidir el
   * siguiente paso SIN depender del query cache (que en el momento de la
   * llamada puede estar stale después de la invalidación).
   */
  onContinue: (aceptoSuperador: boolean) => void;
}

function fmtNumeroAR(n: number): string {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n);
}

const COLOR_PRIMARY = '#002564';
const COLOR_ACCENT = '#6399f8';
const COLOR_ACCENT_BG = 'rgba(99,153,248,0.12)';
const COLOR_ACCENT_BG_LIGHT = 'rgba(99,153,248,0.24)';
// Fondo del bloque MS1 — más claro que #f3f6fb para contrastar mejor con
// el gradient celeste de MS2 sin que la transición sea brusca.
const COLOR_MS1_BG = '#fafbfd';

/**
 * Listas CURADAS de beneficios para mostrar en el paso "Plan" del wizard.
 * Listas y textos definidos por contenido/UX (no derivamos del catálogo
 * backend porque la copy varía levemente — ej. plural "Consultas" vs
 * singular del catálogo). El catálogo del backend sigue siendo source-of
 * -truth para el resto del sistema (por ejemplo, la planilla que firma
 * el titular después).
 *
 * Las palabras entre `**...**` en MS2 se renderean en negrita (ver
 * `renderTextoConBolds`).
 */
const BENEFICIOS_MS1: string[] = [
  'Consultas virtuales de todas las especialidades sin copago',
  'Medicamentos ambulatorios con descuento',
  'Salud mental, kinesiología y odontología',
  'Estudios, diagnósticos y prácticas de alta, mediana y baja complejidad',
  'Emergencias domiciliarias',
  'App MI Medicus, credencial digital para gestiones',
  'Copago en consultas y estudios ambulatorios',
];

const BENEFICIOS_MS2: string[] = [
  'Atención en los centros Medicus y centros Medicus odontológicos',
  '**Sin copago** en consultas y estudios ambulatorios',
  'Medicamentos ambulatorios **con descuento**',
  'Salud mental, kinesiología y odontología',
  'Estudios, diagnósticos y prácticas de alta, mediana y baja complejidad',
  '**Habitación individual** en sanatorios de primer nivel',
  'Seguro de viajero en el exterior',
  'Acompañante en maternidad y en internación',
  'App MI Medicus, credencial digital para gestiones',
  'Emergencias domiciliarias',
];

export function PlanSuperadorStep({ token, onBack, onContinue }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['plan-superador', token],
    queryFn: () => getPlanSuperador(token),
    enabled: !!token,
  });

  // Gate de T&C: aceptar MS2 abre el modal antes de mutar. Solo al aceptar
  // dentro del modal disparamos `mutAceptarMs2.mutate()` y avanzamos al pago.
  const [tycOpen, setTycOpen] = useState(false);

  // Las 3 mutations terminan llamando `onContinue` con el flag MS1/MS2; el
  // wizard padre decide a qué paso avanza (MS2 → pago, MS1 → final).
  const mutAceptarMs2 = useMutation({
    mutationFn: () =>
      elegirPlanSuperador(token, true, { versionDocumento: TYC_MS2_VERSION_ACTUAL }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-superador', token] });
      qc.invalidateQueries({ queryKey: ['onboarding', token] });
      setTycOpen(false);
      onContinue(true);
    },
  });
  const mutInteresContacto = useMutation({
    mutationFn: () => marcarInteresContactoMs2(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-superador', token] });
      qc.invalidateQueries({ queryKey: ['onboarding', token] });
      onContinue(false);
    },
  });
  const mutContinuarMs1 = useMutation({
    mutationFn: () => elegirPlanSuperador(token, false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-superador', token] });
      qc.invalidateQueries({ queryKey: ['onboarding', token] });
      onContinue(false);
    },
  });

  if (q.isLoading) return <p className="text-element-mid">Cargando planes…</p>;
  if (q.isError || !q.data)
    return (
      <p className="status status-error" style={{ display: 'block' }}>
        No se pudo cargar el comparativo de planes.
      </p>
    );

  const { planActual, planSuperador } = q.data.catalogo;
  const precioGrupo = q.data.precioGrupo;
  const beneficiosMs1 = BENEFICIOS_MS1;
  const beneficiosMs2 = BENEFICIOS_MS2;
  // Mostramos siempre el precio SIN IVA (decisión de producto). El catálogo
  // sigue calculando IVA por compatibilidad pero ya no se cobra ni se muestra.
  const precioMensual = precioGrupo.subtotalSinIva;
  const submitting =
    mutAceptarMs2.isPending || mutInteresContacto.isPending || mutContinuarMs1.isPending;

  return (
    // El wizard de Activate.tsx envuelve este componente en un container
    // con padding/max-width. Rompemos ese padding solo acá (negativos
    // horizontales calculados con calc(50% - 50vw)) para que las dos
    // secciones de plan ocupen TODO el ancho de la ventana — como el
    // diseño Figma. El contenido interno mantiene su propio max-width.
    <div style={{ width: '100%' }}>
      <PlanMs1Hero plan={planActual} beneficios={beneficiosMs1} />
      <DescubriMasHint />
      <PlanMs2Upsell
        plan={planSuperador}
        beneficios={beneficiosMs2}
        precioMensual={precioMensual}
        desglose={precioGrupo.desglose}
        submitting={submitting}
        onAceptarMs2={() => setTycOpen(true)}
        onInteresContacto={() => mutInteresContacto.mutate()}
        onContinuarMs1={() => mutContinuarMs1.mutate()}
      />
      {/* Banner de ayuda por WhatsApp, al final del paso (después del MS2).
          Full-bleed con el mismo celeste del cierre del MS2 para que el banner
          (card redondeada) flote integrado y no toque los bordes de la pantalla. */}
      <section style={{ ...FULL_BLEED, background: '#eef3ff' }}>
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: 'clamp(8px, 3vw, 24px) clamp(16px, 5vw, 64px) clamp(48px, 6vw, 88px)',
          }}
        >
          <WhatsAppBanner />
        </div>
      </section>
      <TyCMs2Modal
        open={tycOpen}
        submitting={mutAceptarMs2.isPending}
        onCancelar={() => {
          if (!mutAceptarMs2.isPending) setTycOpen(false);
        }}
        onAceptar={() => mutAceptarMs2.mutate()}
      />
      {(mutAceptarMs2.isError || mutInteresContacto.isError || mutContinuarMs1.isError) && (
        <p
          className="status status-error"
          style={{ display: 'block', marginTop: 16, textAlign: 'center' }}
        >
          No se pudo guardar tu elección. Reintentá.
        </p>
      )}
      <div className="onboarding__actions" style={{ marginTop: 24, justifyContent: 'flex-start' }}>
        <button className="btn btn-ghost" onClick={onBack} disabled={submitting}>
          Volver
        </button>
      </div>
    </div>
  );
}

/** Estilo full-bleed: rompe el padding del wizard padre y ocupa 100vw. */
const FULL_BLEED: React.CSSProperties = {
  position: 'relative',
  left: 'calc(50% - 50vw)',
  width: '100vw',
};

/** Contenedor interno con el max-width tipográfico (como el Figma). */
const INNER_WRAP: React.CSSProperties = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: 'clamp(32px, 5vw, 80px) clamp(20px, 5vw, 64px)',
};

// ---------- Sección 1: hero "Plan MS1" ----------

function PlanMs1Hero({ plan, beneficios }: { plan: PlanComparable; beneficios: string[] }) {
  return (
    <section style={{ ...FULL_BLEED, background: COLOR_MS1_BG }}>
      <div style={INNER_WRAP}>
        <div
          style={{
            display: 'grid',
            // En desktop: dos columnas equilibradas. En pantallas chicas
            // (<880px) hace stack vertical con la card debajo del texto.
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
            gap: 'clamp(32px, 5vw, 80px)',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <Pill icon={<CheckSmall />}>Este es tu plan base</Pill>
            <h1
              style={{
                fontFamily: 'Switzer, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(48px, 9vw, 88px)',
                lineHeight: 0.95,
                letterSpacing: '-0.06em',
                color: COLOR_PRIMARY,
                margin: 0,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {/* "MS" en azul oscuro + número en celeste (intercambio respecto
                  de la versión anterior). */}
              <span style={{ color: COLOR_PRIMARY }}>MS</span>
              <span style={{ color: COLOR_ACCENT }}>{plan.codigo.replace('MS', '')}</span>
            </h1>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 22 }}>
              {beneficios.map((b) => (
                <BeneficioItem key={b} texto={b} variant="ms1" />
              ))}
            </ul>
          </div>
          <PlanMockupCard variant="rojo" />
        </div>
      </div>
    </section>
  );
}

/**
 * Hint sutil de scroll entre las dos secciones. Comparte el fondo de MS1
 * para no romper la continuidad visual. Al hacer click, scrollea suavemente
 * hasta la sección MS2 (id `plan-ms2-upsell`).
 */
function DescubriMasHint() {
  const handleClick = () => {
    const target = document.getElementById('plan-ms2-upsell');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Ir al Plan Superador"
      style={{
        ...FULL_BLEED,
        background: COLOR_MS1_BG,
        border: 'none',
        padding: '12px 0 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        opacity: 0.32,
        color: COLOR_PRIMARY,
        cursor: 'pointer',
        // El usuario hover sube ligeramente el opacity para que se note que
        // es clickable (sin alejarse del look "hint sutil" del Figma).
        transition: 'opacity 0.25s ease-out',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.55')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.32')}
    >
      <span
        style={{
          fontFamily: 'Satoshi, sans-serif',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        Descubrí más
      </span>
      <span className="onb-hint-bounce" style={{ display: 'inline-flex' }}>
        <ChevronDown size={14} strokeWidth={2.2} aria-hidden />
      </span>
    </button>
  );
}

/**
 * Desglose del precio, una fila por integrante.
 *
 * El MS2 se cotiza sobre el grupo familiar, así que el titular tiene que poder
 * ver POR QUIÉN está pagando: si el grupo cambió (alguien se dio de baja), el
 * total cambia y sin este detalle no hay forma de entender por qué. La lista
 * viene del backend ya filtrada — los dados de baja no se cotizan y no se
 * muestran. Importes sin IVA, igual que el total.
 */
function DesglosePorPersona({
  desglose,
  total,
}: {
  desglose: DesgloseIntegrantePrecio[];
  total: number;
}) {
  if (desglose.length === 0) return null;
  const fila: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 16,
    padding: '10px 0',
    fontFamily: 'Switzer, sans-serif',
    fontSize: 'clamp(14px, 1.6vw, 17px)',
  };
  return (
    <div
      style={{
        maxWidth: 520,
        margin: '28px auto 0',
        textAlign: 'left',
        borderTop: '1px solid rgba(255,255,255,0.25)',
      }}
    >
      <p
        style={{
          fontFamily: 'Switzer, sans-serif',
          fontWeight: 600,
          fontSize: 'clamp(13px, 1.5vw, 15px)',
          opacity: 0.75,
          margin: '16px 0 4px',
        }}
      >
        Tu grupo
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {desglose.map((d) => (
          <li key={`${d.nombre}-${d.edad}`} style={fila}>
            <span>
              {d.nombre}
              <span style={{ opacity: 0.6 }}> · {d.edad} años</span>
            </span>
            <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              ${fmtNumeroAR(d.precioSinIva)}
            </span>
          </li>
        ))}
      </ul>
      <div
        style={{
          ...fila,
          borderTop: '1px solid rgba(255,255,255,0.25)',
          fontWeight: 800,
          marginTop: 4,
        }}
      >
        <span>Total por mes</span>
        <span style={{ whiteSpace: 'nowrap' }}>${fmtNumeroAR(total)}</span>
      </div>
    </div>
  );
}

// ---------- Sección 2: upsell MS2 ----------

function PlanMs2Upsell({
  plan,
  beneficios,
  precioMensual,
  desglose,
  submitting,
  onAceptarMs2,
  onInteresContacto,
  onContinuarMs1,
}: {
  plan: PlanComparable;
  beneficios: string[];
  precioMensual: number;
  desglose: DesgloseIntegrantePrecio[];
  submitting: boolean;
  onAceptarMs2: () => void;
  onInteresContacto: () => void;
  onContinuarMs1: () => void;
}) {
  // Se dispara cuando la sección MS2 entra al viewport. Aplica la clase
  // `onb-revealed` que activa las animaciones internas (título reveal +
  // stagger de bullets). One-shot: no re-anima al volver a scrollear.
  const [revealRef, revealed] = useInViewport<HTMLElement>({ rootMargin: '-80px 0px' });
  return (
    <section
      ref={revealRef}
      id="plan-ms2-upsell"
      className={revealed ? 'onb-revealed' : undefined}
      style={{
        ...FULL_BLEED,
        background: 'linear-gradient(180deg, #eaf0ff 0%, #eef3ff 40%, #eef3ff 100%)',
      }}
    >
      <div style={INNER_WRAP}>
        <header style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2
            className="onb-title-reveal"
            style={{
              fontFamily: 'Switzer, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(20px, 2.8vw, 36px)',
              lineHeight: 1.15,
              letterSpacing: '-0.03em',
              color: COLOR_PRIMARY,
              textTransform: 'uppercase',
              margin: 0,
              display: 'inline-block',
            }}
          >
            Accedé a un plan superior por
          </h2>
          <p
            style={{
              fontFamily: 'Switzer, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(40px, 6.5vw, 88px)',
              lineHeight: 1,
              letterSpacing: '-0.05em',
              color: COLOR_ACCENT,
              margin: '12px 0 0',
            }}
          >
            ${fmtNumeroAR(precioMensual)}
          </p>
          <DesglosePorPersona desglose={desglose} total={precioMensual} />
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
            gap: 'clamp(32px, 5vw, 80px)',
            // `start` alinea el top de la columna izquierda (card) con el
            // top de la columna derecha (bullets) — antes era `center` y
            // dejaba los bullets en el medio de la card.
            alignItems: 'start',
            marginBottom: 40,
          }}
        >
          {/* Columna IZQUIERDA: card con blur decorativo detrás + los 3 CTAs
              debajo. Antes los botones iban con los bullets a la derecha. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div style={{ position: 'relative' }}>
              {/* Copia del card con blur — efecto de profundidad. Con la
                  remoción del pill arriba, el inset arranca en 0 (antes
                  desplazaba 60px para esquivar el pill). */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  filter: 'blur(50px)',
                  opacity: 0.34,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                <PlanMockupCard variant="verde" />
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <PlanMockupCard variant="verde" />
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 12,
                width: '100%',
              }}
            >
              <button
                type="button"
                onClick={onAceptarMs2}
                disabled={submitting}
                style={{
                  background: COLOR_ACCENT,
                  color: 'white',
                  border: 'none',
                  borderRadius: 800,
                  padding: '16px 24px',
                  fontFamily: 'Satoshi, sans-serif',
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  boxShadow: 'inset 0 2px 0 0 rgba(255,255,255,0.12)',
                  opacity: submitting ? 0.6 : 1,
                  height: 56,
                }}
              >
                Acceder a plan {plan.codigo}
              </button>
              <button
                type="button"
                onClick={onInteresContacto}
                disabled={submitting}
                style={{
                  background: 'white',
                  color: COLOR_PRIMARY,
                  border: 'none',
                  borderRadius: 800,
                  padding: '16px 24px',
                  fontFamily: 'Satoshi, sans-serif',
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                  height: 56,
                }}
              >
                Quiero que me contacten
              </button>
              <button
                type="button"
                onClick={onContinuarMs1}
                disabled={submitting}
                style={{
                  background: 'transparent',
                  color: COLOR_PRIMARY,
                  border: '1px solid rgba(0,37,100,0.15)',
                  borderRadius: 800,
                  padding: '16px 24px',
                  fontFamily: 'Satoshi, sans-serif',
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                  height: 56,
                }}
              >
                Continuar con mi plan base
              </button>
            </div>
          </div>
          {/* Columna DERECHA: solo los bullets. Arranca arriba (alignItems
              de la grid en `start`) para coincidir con el top de la card. */}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 22 }}>
            {beneficios.map((b, i) => (
              <BeneficioItem
                key={b}
                texto={b}
                variant="ms2"
                animationDelay={`${600 + i * 80}ms`}
              />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------- Building blocks ----------

function Pill({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        background: COLOR_ACCENT_BG,
        borderRadius: 800,
        padding: icon ? '8px 24px 8px 8px' : '14px 24px',
        color: COLOR_ACCENT,
        fontWeight: 700,
        fontSize: 16,
        letterSpacing: '-0.5px',
        alignSelf: 'flex-start',
      }}
    >
      {icon && (
        <span
          style={{
            background: COLOR_ACCENT_BG_LIGHT,
            borderRadius: 42,
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLOR_ACCENT,
          }}
        >
          {icon}
        </span>
      )}
      {children}
    </div>
  );
}

function CheckSmall() {
  return <CheckIcon size={16} strokeWidth={3} />;
}

/**
 * Parser mini de markdown bold (**palabra**). Lo usamos en los beneficios
 * del MS2 para destacar las palabras que el diseño marca con negrita
 * (ej. "sin copago", "con descuento", "Habitación individual"). Solo
 * soportamos `**...**` — no es un parser completo de markdown, no hace
 * falta. Si el texto NO trae asteriscos, lo renderiza tal cual.
 */
function renderTextoConBolds(texto: string): React.ReactNode {
  const partes = texto.split(/(\*\*[^*]+\*\*)/g);
  return partes.map((parte, i) => {
    if (parte.startsWith('**') && parte.endsWith('**')) {
      return <strong key={i}>{parte.slice(2, -2)}</strong>;
    }
    return <span key={i}>{parte}</span>;
  });
}

function BeneficioItem({
  texto,
  variant = 'ms1',
  animationDelay,
}: {
  texto: string;
  variant?: 'ms1' | 'ms2';
  /** Si se pasa, el item se marca como `onb-bullet` (anim de entrada) con
   * ese delay. Solo se usa en MS2 para staggear la aparición. */
  animationDelay?: string;
}) {
  // El Figma usa íconos más grandes (32px) y texto 20px en MS2 para acentuar
  // el upsell; MS1 mantiene 24px + 18px como referencia secundaria.
  const isMs2 = variant === 'ms2';
  const dotSize = isMs2 ? 32 : 24;
  const fontSize = isMs2 ? 'clamp(16px, 1.6vw, 20px)' : 'clamp(15px, 1.4vw, 18px)';
  return (
    <li
      className={animationDelay ? 'onb-bullet' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize,
        color: COLOR_PRIMARY,
        letterSpacing: '-0.025em',
        lineHeight: 1.35,
        fontFamily: 'Satoshi, sans-serif',
        fontWeight: 500,
        ...(animationDelay ? { animationDelay } : {}),
      }}
    >
      <span
        style={{
          background: COLOR_ACCENT,
          borderRadius: 9999,
          width: dotSize,
          height: dotSize,
          minWidth: dotSize,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <CheckIcon size={isMs2 ? 14 : 12} strokeWidth={3} />
      </span>
      <span>{renderTextoConBolds(texto)}</span>
    </li>
  );
}

// `PlanMockupCard` y `CredencialMedicusLogo` viven ahora en
// `./PlanMockupCard.tsx` para que `PagoStep` también los pueda usar.
